if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      connectedCallback() {
        this.onIntent = event => {
          const thumbnail = event.target.closest('[data-target]');
          if (thumbnail && this.elements.thumbnails?.contains(thumbnail)) this.warmImage(this.elements.viewer.querySelector(`[data-media-id="${thumbnail.dataset.target}"]`));
        };
        this.elements.thumbnails?.addEventListener('pointerover', this.onIntent);
        this.elements.thumbnails?.addEventListener('focusin', this.onIntent);
        if ('IntersectionObserver' in window) {
          this.warmObserver = new IntersectionObserver(entries => {
            if (entries.some(entry => entry.isIntersecting)) { this.warmObserver.disconnect(); this.scheduleWarmNext(); }
          });
          this.warmObserver.observe(this);
        } else this.scheduleWarmNext();
      }

      disconnectedCallback() {
        this.warmObserver?.disconnect(); clearTimeout(this.warmTimer); clearTimeout(this.announceTimer);
        this.pendingImage?.removeEventListener('load', this.pendingAnnouncement);
        if (this.warmIdle && window.cancelIdleCallback) cancelIdleCallback(this.warmIdle);
        this.elements.thumbnails?.removeEventListener('pointerover', this.onIntent);
        this.elements.thumbnails?.removeEventListener('focusin', this.onIntent);
      }

      warmImage(item, priority = 'low') {
        const image = item?.querySelector('.product__modal-opener--image img');
        if (image) { image.fetchPriority = priority; image.loading = 'eager'; }
        return image;
      }

      scheduleWarmNext(active = this.elements.viewer.querySelector('.is-active')) {
        clearTimeout(this.warmTimer);
        if (this.warmIdle && window.cancelIdleCallback) cancelIdleCallback(this.warmIdle);
        const warm = () => {
          if (!this.isConnected) return;
          const items = [...this.elements.viewer.querySelectorAll('.product__media-item')];
          const index = Math.max(0, items.indexOf(active));
          if (items.length > 1) this.warmImage(items[(index + 1) % items.length]);
        };
        if ('requestIdleCallback' in window) this.warmIdle = requestIdleCallback(warm, { timeout: 1200 });
        else this.warmTimer = setTimeout(warm, 300);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.scheduleWarmNext(event.detail.currentElement);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.warmImage(activeMedia, 'high');
        this.scheduleWarmNext(activeMedia);
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion || !position) return;
        this.pendingImage?.removeEventListener('load', this.pendingAnnouncement);
        const announce = () => {
          if (!this.isConnected || !activeItem.classList.contains('is-active')) return;
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.textContent = (window.accessibilityStrings?.imageAvailable || 'Zdjęcie [index] jest dostępne').replace('[index]', position);
          clearTimeout(this.announceTimer);
          this.announceTimer = setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
        };
        if (image.complete && image.naturalWidth) announce();
        else { this.pendingImage = image; this.pendingAnnouncement = announce; image.addEventListener('load', announce, { once: true }); }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
